import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Navbar } from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Loader2, Plus, History, FileText, TrendingUp, 
  Calendar, ArrowRight, Sparkles 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 75) return 'text-lime-600';
    if (score >= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
        </div>
      </div>
    );
  }

  const { usage, recent_analyses } = dashboardData || {};
  const analysesPercent = usage ? (usage.analyses_used / usage.analyses_limit) * 100 : 0;
  const exportsPercent = usage?.exports_limit > 0 
    ? (usage.exports_used / usage.exports_limit) * 100 
    : 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading font-bold text-2xl text-slate-900">
              Welcome back, {user?.name?.split(' ')[0] || 'there'}!
            </h1>
            <p className="text-slate-600">
              You're on the <span className="font-medium capitalize">{user?.plan || 'Free'}</span> plan
            </p>
          </div>
          <Button
            onClick={() => navigate('/app/analyze')}
            className="bg-slate-900 text-white hover:bg-slate-800"
            data-testid="new-analysis-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Analysis
          </Button>
        </div>

        {/* Usage Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-white border border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-slate-600">Analyses Used</span>
                <FileText className="w-5 h-5 text-slate-400" />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="font-heading font-bold text-2xl text-slate-900">
                    {usage?.analyses_used || 0}
                  </span>
                  <span className="text-slate-500">/ {usage?.analyses_limit || 3}</span>
                </div>
                <Progress value={analysesPercent} className="h-2" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-slate-600">Exports Used</span>
                <TrendingUp className="w-5 h-5 text-slate-400" />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="font-heading font-bold text-2xl text-slate-900">
                    {usage?.exports_used || 0}
                  </span>
                  <span className="text-slate-500">
                    / {usage?.exports_limit === -1 ? '∞' : usage?.exports_limit || 1}
                  </span>
                </div>
                <Progress 
                  value={usage?.exports_limit === -1 ? 0 : exportsPercent} 
                  className="h-2" 
                />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-slate-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-slate-600">Resets On</span>
                <Calendar className="w-5 h-5 text-slate-400" />
              </div>
              <div>
                <span className="font-heading font-bold text-2xl text-slate-900">
                  {usage?.next_reset_date 
                    ? new Date(usage.next_reset_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    : 'N/A'
                  }
                </span>
                <p className="text-sm text-slate-500 mt-1">Monthly reset date</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Upgrade Banner (for free users) */}
        {user?.plan === 'free' && (
          <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white mb-8">
            <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-lg">Upgrade to Pro</h3>
                  <p className="text-slate-300 text-sm">Get 50 analyses/month and unlimited exports</p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/pricing')}
                className="bg-white text-slate-900 hover:bg-slate-100"
              >
                View Plans
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Recent Analyses */}
        <Card className="bg-white border border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Recent Analyses</CardTitle>
            <Link to="/app/history">
              <Button variant="ghost" size="sm" className="text-slate-600">
                <History className="w-4 h-4 mr-2" />
                View All
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recent_analyses && recent_analyses.length > 0 ? (
              <div className="space-y-4">
                {recent_analyses.map((analysis) => (
                  <Link 
                    key={analysis.id}
                    to={`/app/results/${analysis.id}`}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-white rounded-lg border border-slate-200 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {analysis.job_title || 'Untitled Analysis'}
                        </p>
                        <p className="text-sm text-slate-500">
                          {analysis.company || 'Company not specified'} • {
                            new Date(analysis.timestamp).toLocaleDateString()
                          }
                        </p>
                      </div>
                    </div>
                    <Badge className={`${getScoreColor(analysis.overall_score)} bg-transparent`}>
                      {analysis.overall_score}%
                    </Badge>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-medium text-slate-900 mb-2">No analyses yet</h3>
                <p className="text-slate-500 text-sm mb-4">
                  Start by analyzing your resume against a job description
                </p>
                <Button onClick={() => navigate('/app/analyze')} className="bg-slate-900 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  New Analysis
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
